import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/Avatar";
import { BodyText } from "@/components/ui/Text";
import { cn, getInitials } from "@/lib/utils";
import { formatDate } from "date-fns";
import type {
  ChatParticipant,
  TextUniversalChatMessage,
} from "../types/universalChatTypes";
import { MarkdownMessage } from "./MarkdownMessage";
import { TimestampWithStatus } from "./TimestampWithStatus";
import { mapTagsToHeading } from "./messageUtils";

interface TextMessageProps {
  message: TextUniversalChatMessage;
  includeAvatar?: boolean;
  participant?: ChatParticipant;
}

export const TextMessage = ({
  message,
  includeAvatar = false,
  participant,
}: TextMessageProps) => {
  const senderType = message.senderType;
  const isPatient = senderType === "patient";
  const isSystem = senderType === "system";
  const isClinician = senderType === "clinician";
  const isMarkdown = Boolean(message.body.markdown);
  const content = message.body.text || "";

  // Get heading from metadata tags
  const heading = mapTagsToHeading(message.metadata?.tags);

  // System messages have different rendering
  if (isSystem) {
    return (
      <div className="flex flex-col justify-center items-center text-center">
        {isMarkdown ? (
          <div className="w-full max-w-lg">
            <MarkdownMessage
              content={content}
              heading={heading}
              align="center"
            />
          </div>
        ) : (
          <BodyText size="xs" textColor="primary" italic>
            {content}
          </BodyText>
        )}
        <BodyText size="xs" textColor="tertiary">
          {formatDate(new Date(message.createdAt), "MM/dd/yyyy h:mm a")}
        </BodyText>
      </div>
    );
  }

  // Get participant name and initials for human messages
  const participantName =
    participant?.displayName || (isPatient ? "Patient" : "Doctor");
  const participantInitials = participant?.displayName
    ? getInitials(participant.displayName).slice(0, 2)
    : isPatient
      ? "PT"
      : "DR";

  return (
    <div className="flex flex-col">
      {/* Avatar - show for both patients and clinicians when includeAvatar is true */}
      {includeAvatar && (
        <div
          className={cn(
            "flex gap-2 items-center",
            isPatient ? "justify-start" : "justify-end",
          )}
        >
          {/* For clinicians, show name first then avatar */}
          {!isPatient && (
            <BodyText size="xs" textColor="secondary">
              {participantName}
            </BodyText>
          )}
          <Avatar className="w-6 h-6">
            {participant?.photoUrl && (
              <AvatarImage src={participant.photoUrl} />
            )}
            <AvatarFallback
              className={cn(
                "text-xs font-medium",
                isPatient ? "bg-accent-primary" : "text-white bg-primary-green",
              )}
            >
              {participantInitials}
            </AvatarFallback>
          </Avatar>
          {/* For patients, show avatar first then name */}
          {isPatient && (
            <BodyText size="xs" textColor="secondary">
              {participantName}
            </BodyText>
          )}
        </div>
      )}

      {/* Message content */}
      <div
        className={cn("flex mt-2", isPatient ? "justify-start" : "justify-end")}
      >
        <div
          className={cn(
            "flex flex-col gap-1",
            isMarkdown
              ? "max-w-lg w-full"
              : "p-3 rounded-lg max-w-xs bg-info break-words",
          )}
        >
          {isMarkdown ? (
            <MarkdownMessage
              content={content}
              heading={heading}
              align={isPatient ? "left" : "right"}
            />
          ) : (
            <BodyText size="sm" preserveLineBreaks>
              {content}
            </BodyText>
          )}
        </div>
      </div>

      {/* Timestamp */}
      {isClinician ? (
        <TimestampWithStatus
          timestamp={message.createdAt}
          status={message.processingStatus ?? null}
          alignmentClass={"text-right"}
        />
      ) : (
        <div className={cn("text-left")}>
          <BodyText size="xs" textColor="tertiary">
            {formatDate(new Date(message.createdAt), "MM/dd/yyyy h:mm a")}
          </BodyText>
        </div>
      )}
    </div>
  );
};
